import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CalendarCheck,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Video,
  MapPin,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { bookingApi } from "@/lib/crmApi";
import { cn } from "@/lib/utils";

/**
 * Public booking page — no auth, served at /booking/:token.
 *
 * The lead receives a personal URL via WhatsApp. They land here, see free
 * slots grouped by day, click one, confirm. The RDV is then registered in
 * the CRM exactly like an AI-detected one.
 */
export default function BookingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selected slot (an entry from data.slots) + reservation state
  const [selected, setSelected] = useState(null);
  const [reserving, setReserving] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // { rdv_label, rdv_kind }

  useEffect(() => {
    let cancelled = false;
    bookingApi
      .publicInfo(token)
      .then((d) => {
        if (cancelled) return;
        setData(d);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Lien invalide ou expiré");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Group slots by local_date for a clean day-by-day display
  const slotsByDay = useMemo(() => {
    if (!data?.slots) return [];
    const map = new Map();
    for (const s of data.slots) {
      if (!map.has(s.local_date)) map.set(s.local_date, []);
      map.get(s.local_date).push(s);
    }
    return Array.from(map.entries()).map(([date, slots]) => {
      const ref = new Date(date + "T12:00:00");
      const weekday = ref.toLocaleDateString("fr-FR", { weekday: "long" });
      const day = ref.getDate();
      const month = ref.toLocaleDateString("fr-FR", { month: "long" });
      const niceDate =
        weekday[0].toUpperCase() + weekday.slice(1) + ` ${day} ${month}`;
      return { date, niceDate, slots };
    });
  }, [data]);

  const reserve = async () => {
    if (!selected) return;
    setReserving(true);
    setError(null);
    try {
      const r = await bookingApi.publicReserve(token, selected.iso);
      setConfirmed(r);
    } catch (e) {
      setError(e.message || "Erreur lors de la réservation");
    } finally {
      setReserving(false);
    }
  };

  // -------------------------------------------------------- Loading / Error
  if (loading) {
    return (
      <FullPage>
        <Loader2 className="h-8 w-8 animate-spin text-accent-navy" />
        <p className="mt-3 text-sm text-muted-foreground">Chargement…</p>
      </FullPage>
    );
  }
  if (error && !confirmed) {
    return (
      <FullPage>
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-950/40">
          <AlertCircle className="h-6 w-6" />
        </div>
        <h1 className="mt-4 font-heading text-xl font-bold">Oups…</h1>
        <p className="mt-1 text-sm text-muted-foreground">{error}</p>
        <p className="mt-3 text-xs text-muted-foreground">
          Si le problème persiste, contactez-nous directement.
        </p>
      </FullPage>
    );
  }

  // -------------------------------------------------------- Confirmation
  if (confirmed) {
    return (
      <FullPage>
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="mt-4 font-heading text-2xl font-bold">RDV confirmé !</h1>
        <p className="mt-2 max-w-md text-center text-sm text-muted-foreground">
          {data?.lead?.prenom ? `Merci ${data.lead.prenom} 👋` : "Merci"} — votre rendez-vous est noté.
        </p>
        <div className="mt-6 flex w-full max-w-md flex-col items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 dark:border-emerald-900/40 dark:bg-emerald-950/20">
          <div className="flex items-center gap-2 text-base font-bold">
            <CalendarCheck className="h-5 w-5 text-emerald-600" />
            {confirmed.rdv_label}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {confirmed.rdv_kind === "visio" ? (
              <>
                <Video className="h-3.5 w-3.5" /> Visioconférence (Google Meet ou WhatsApp)
              </>
            ) : (
              <>
                <MapPin className="h-3.5 w-3.5" /> Showroom à Dakar
              </>
            )}
          </div>
        </div>
        <p className="mt-6 max-w-md text-center text-xs text-muted-foreground">
          {confirmed.rdv_kind === "visio"
            ? "Un lien de visio vous sera envoyé sur WhatsApp avant le rendez-vous."
            : "L'adresse exacte du showroom vous sera envoyée sur WhatsApp avant le rendez-vous."}
          <br />
          Vous recevrez aussi un rappel la veille.
        </p>
      </FullPage>
    );
  }

  // -------------------------------------------------------- Slot picker
  const lead = data?.lead || {};
  const company = data?.company_name || "SOGI SARL";
  const isVisio = lead.rdv_kind === "visio";
  const noSlots = slotsByDay.length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-zinc-100 dark:from-zinc-950 dark:to-zinc-900 px-4 py-6 sm:py-12">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <header className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-navy text-white shadow-lg dark:bg-accent-gold dark:text-zinc-900">
            <CalendarIcon className="h-7 w-7" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            Prendre rendez-vous
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Avec {company}
            {lead.programme ? ` · ${lead.programme}` : ""}
          </p>
          {lead.prenom && (
            <p className="mt-3 text-base">
              Bonjour <b>{lead.prenom}</b> 👋
            </p>
          )}
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3 py-1 text-[11px] font-medium text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
            {isVisio ? (
              <>
                <Video className="h-3 w-3" /> Rendez-vous en visio (à distance)
              </>
            ) : (
              <>
                <MapPin className="h-3 w-3" /> Rendez-vous au showroom (Dakar)
              </>
            )}
          </div>
        </header>

        {/* Existing RDV banner */}
        {lead.current_rdv_at && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
            <b>RDV déjà programmé :</b> {lead.current_rdv_label}
            <p className="mt-1 text-xs text-muted-foreground">
              En choisissant un nouveau créneau, ce RDV sera remplacé.
            </p>
          </div>
        )}

        {/* Slots */}
        {noSlots ? (
          <div className="rounded-2xl border border-dashed border-border/60 bg-card p-8 text-center">
            <Clock className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-sm font-semibold">Aucun créneau disponible</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Tous les créneaux sont actuellement pris. Contactez-nous sur WhatsApp pour qu'on vous trouve un horaire.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {slotsByDay.map((day) => (
                <section
                  key={day.date}
                  className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm sm:p-5"
                  data-testid={`booking-day-${day.date}`}
                >
                  <h3 className="font-heading mb-3 text-sm font-bold text-foreground/80">
                    {day.niceDate}
                  </h3>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {day.slots.map((s) => {
                      const isSelected = selected?.iso === s.iso;
                      return (
                        <button
                          key={s.iso}
                          type="button"
                          onClick={() => setSelected(s)}
                          data-testid={`booking-slot-${s.iso}`}
                          className={cn(
                            "rounded-lg border px-3 py-2 text-sm font-medium tabular-nums transition",
                            isSelected
                              ? "border-accent-navy bg-accent-navy text-white shadow dark:border-accent-gold dark:bg-accent-gold dark:text-zinc-900"
                              : "border-border/60 bg-background hover:border-accent-navy/60 hover:bg-accent-navy/5 dark:hover:border-accent-gold/60"
                          )}
                        >
                          {s.local_time}
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            {/* Sticky confirm bar */}
            <div className="sticky bottom-0 z-10 mt-6 -mx-4 sm:mx-0">
              <div className="mx-auto max-w-2xl">
                <div className="rounded-t-2xl border border-b-0 border-border/60 bg-card p-4 shadow-lg sm:rounded-2xl sm:border-b">
                  {selected ? (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Créneau choisi
                        </div>
                        <div className="font-heading text-base font-bold">
                          {selected.label}
                        </div>
                      </div>
                      <Button
                        size="lg"
                        onClick={reserve}
                        disabled={reserving}
                        className="gap-2"
                        data-testid="booking-confirm"
                      >
                        {reserving ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Réservation…
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4" />
                            Confirmer ce créneau
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center text-sm text-muted-foreground">
                      ↑ Choisissez une heure ci-dessus
                    </div>
                  )}
                  {error && (
                    <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
                      {error}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <footer className="mt-10 text-center text-[11px] text-muted-foreground">
          Heures affichées en heure de Dakar (GMT)
        </footer>
      </div>
    </div>
  );
}

function FullPage({ children }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-zinc-50 to-zinc-100 px-4 py-12 dark:from-zinc-950 dark:to-zinc-900">
      {children}
    </div>
  );
}
